import { Box, Stack, Typography } from '@mui/material';
import { BASE_COLORS } from 'lib/functions/const/colors';
import React from 'react';

import { hexToRGBA } from '../BarChart';
import TableCard from '../Card/TableCard';

const BAR_WIDTH = 15;

const sizes = {
  small: {
    labelSize: '0.65rem',
    valueSize: '0.96rem',
    valueLabelSize: '0.55rem',
  },
  medium: {
    labelSize: '0.75rem',
    valueSize: '1.2rem',
    valueLabelSize: '0.625rem',
  },
};

export type StatAlertVariant =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'grey';

const VARIANT_COLORS: Record<StatAlertVariant, string> = {
  primary: BASE_COLORS.blue,
  success: BASE_COLORS.green,
  error: BASE_COLORS.red,
  warning: BASE_COLORS.orange,
  info: BASE_COLORS.lightBlue,
  grey: BASE_COLORS.gray,
};

interface StatAlertProps {
  label: string;
  value: number | string;
  valueLabel: string;
  variant?: StatAlertVariant;
  color?: string;
  size?: 'small' | 'medium';
}

const StatAlert: React.FC<StatAlertProps> = ({
  label,
  value,
  valueLabel,
  variant = 'primary',
  color: colorProp,
  size = 'medium',
}) => {
  const barColor =
    colorProp != null
      ? (VARIANT_COLORS[colorProp as StatAlertVariant] ?? colorProp)
      : VARIANT_COLORS[variant];

  return (
    <TableCard size="small" sx={{ overflow: 'hidden', p: 0 }}>
      <Box sx={{ display: 'flex', minHeight: 56 }}>
        <Box sx={{ flex: 1, py: 1.5, px: 2 }}>
          <Typography
            variant="caption"
            color="text.primary"
            fontSize={sizes[size].labelSize}
            display="block"
          >
            {label}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Typography
              variant="h4"
              color="text.primary"
              fontSize={sizes[size].valueSize}
            >
              {value}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: sizes[size].valueLabelSize,
                fontWeight: 500,
                alignSelf: 'end',
              }}
            >
              {valueLabel}
            </Typography>
          </Stack>
        </Box>
        <Box
          sx={{
            width: BAR_WIDTH,
            minWidth: BAR_WIDTH,
            backgroundColor: hexToRGBA(barColor, 0.12),
            margin: 1,
            marginLeft: 0,
            borderRadius: '12px 12px 12px 0',
            alignSelf: 'stretch',
          }}
        />
      </Box>
    </TableCard>
  );
};

export default StatAlert;
